import React, { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { CalendarClock, Download, Info, Search } from "lucide-react";
import * as XLSX from "xlsx";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

interface RenewalRow {
    senta_id: string;
    client_name: string;
    client_ref: string | null;
    client_state: string | null;
    owner: string | null;
    renewal_date: string;
    wip_12m: number;
    fees_12m: number;
    fees_minus_wip: number;
    recovery_pct: number;
}

interface RenewalsPayload {
    renewal_month: string;
    window_start: string;
    count: number;
    rows: RenewalRow[];
    available_months?: { month: string; clients: number }[];
    overdue_clients?: number;
}

// Default to next calendar month — the team runs this at the start of each
// month for the clients renewing the following month.
const nextMonth = (): string => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// Every client_state code present in Senta, so nothing renders as a raw code.
const CLIENT_STATE_LABEL: Record<string, string> = {
    "l-clientstate-c": "Client",
    "l-clientstate-p": "Prospect",
    "l-clientstate-a": "Archived",
    "clientstate-onboarding": "Onboarding",
    "clientstate-onboardingaccounting": "Onboarding (Accounting)",
    "clientstate-suspended": "Suspended",
    "clientstate-onhold": "On Hold",
    "clientstate-strikingoff": "Striking Off",
    "clientstate-onceoffnotpayingforongoingservices": "Once-off (no ongoing services)",
    "clientstate-duplicate": "Duplicate",
    "clientstate-internalrelatedtolfrk": "Internal / Related",
    "clientstate-test": "Test",
};
const clientStateLabel = (code: string | null) => (code ? CLIENT_STATE_LABEL[code] || code : "—");

const eur = (n: number) =>
    n.toLocaleString("en-IE", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 });

const recoveryColor = (pct: number, wip: number) => {
    if (wip === 0) return "var(--text-muted)";
    if (pct >= 100) return "#2ea043";
    if (pct >= 70) return "#d29922";
    return "#f85149";
};

const monthLabel = (ym: string): string => {
    const [y, m] = ym.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("en-IE", { month: "long", year: "numeric" });
};

const customStyles = {
    table: { style: { backgroundColor: "var(--bg-secondary)", borderRadius: "8px" } },
    headRow: {
        style: {
            backgroundColor: "var(--bg-tertiary)",
            borderBottomColor: "var(--border-color)",
            color: "var(--text-primary)",
            fontWeight: 600,
            fontSize: "0.85rem",
        },
    },
    headCells: { style: { color: "var(--text-secondary)", paddingLeft: "16px", paddingRight: "16px" } },
    rows: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
        highlightOnHoverStyle: {
            backgroundColor: "var(--bg-hover)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
    },
    cells: { style: { color: "var(--text-primary)", paddingLeft: "16px", paddingRight: "16px" } },
    pagination: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-secondary)",
            borderTopColor: "var(--border-color)",
        },
        pageButtonsStyle: {
            color: "var(--text-secondary)",
            fill: "var(--text-secondary)",
            "&:hover:not(:disabled)": { backgroundColor: "var(--bg-hover)" },
            "&:disabled": { opacity: 0.4 },
        },
    },
    noData: { style: { backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" } },
};

const RenewalsWipFees: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();

    const [payload, setPayload] = useState<RenewalsPayload | null>(null);
    const [renewalMonth, setRenewalMonth] = useState<string>(nextMonth());
    const [search, setSearch] = useState("");
    // Client-state filter. Defaults to active clients only — suspended,
    // striking-off, on-hold etc. renewals aren't ones to chase, but users can
    // switch them on to see them.
    const [stateFilter, setStateFilter] = useState<Set<string>>(new Set(["l-clientstate-c"]));
    const [showHelp, setShowHelp] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            const token = await acquireTokenSafe(instance, accounts);
            if (!token) {
                setError("Your session has expired — please refresh the page to sign in again.");
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const url = `${getApiBaseUrl()}/api/renewals-wip-fees/data?renewalMonth=${renewalMonth}`;
                const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) throw new Error(`Request failed (${res.status})`);
                setPayload(await res.json());
            } catch (err: any) {
                setError(err?.message || "Failed to load renewals data");
                setPayload(null);
            } finally {
                setLoading(false);
            }
        };
        if (/^\d{4}-\d{2}$/.test(renewalMonth)) fetchData();
    }, [instance, accounts, renewalMonth, setLoading]);

    // Dropdown options: every month Senta has renewals in, plus the currently
    // selected month (so the control never shows a blank value).
    const monthOptions = useMemo(() => {
        const opts = [...(payload?.available_months || [])];
        if (!opts.some((o) => o.month === renewalMonth)) {
            opts.push({ month: renewalMonth, clients: payload?.renewal_month === renewalMonth ? payload.count : 0 });
        }
        return opts.sort((a, b) => a.month.localeCompare(b.month));
    }, [payload, renewalMonth]);

    // Every client state present in the selected month, with counts, active
    // clients first then by frequency.
    const stateOptions = useMemo(() => {
        const counts = new Map<string, number>();
        for (const r of payload?.rows || []) {
            const k = r.client_state || "";
            counts.set(k, (counts.get(k) || 0) + 1);
        }
        return Array.from(counts.entries())
            .map(([code, count]) => ({ code, count }))
            .sort((a, b) =>
                a.code === "l-clientstate-c" ? -1 : b.code === "l-clientstate-c" ? 1 : b.count - a.count
            );
    }, [payload]);

    const toggleState = (code: string) =>
        setStateFilter((prev) => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code);
            else next.add(code);
            return next;
        });

    const stateFiltered = useMemo(
        () => (payload?.rows || []).filter((r) => stateFilter.has(r.client_state || "")),
        [payload, stateFilter]
    );

    const rows = useMemo(() => {
        if (!search) return stateFiltered;
        const q = search.toLowerCase();
        return stateFiltered.filter(
            (r) =>
                r.client_name?.toLowerCase().includes(q) ||
                r.senta_id?.toLowerCase().includes(q) ||
                (r.client_ref || "").toLowerCase().includes(q) ||
                (r.owner || "").toLowerCase().includes(q)
        );
    }, [stateFiltered, search]);

    const totals = useMemo(() => {
        const all = stateFiltered;
        const wip = all.reduce((s, r) => s + r.wip_12m, 0);
        const fees = all.reduce((s, r) => s + r.fees_12m, 0);
        return {
            clients: all.length,
            wip,
            fees,
            recovery: wip > 0 ? Math.round((fees / wip) * 100) : 0,
            underRecovered: all.filter((r) => r.wip_12m > 0 && r.recovery_pct < 100).length,
        };
    }, [stateFiltered]);

    const columns: TableColumn<RenewalRow>[] = [
        {
            name: "Client",
            selector: (r) => r.client_name || "",
            sortable: true,
            grow: 3,
            cell: (r) => (
                <div>
                    <div className="cell-primary">{r.client_name || "—"}</div>
                    <div className="cell-secondary" style={{ fontSize: 11 }}>
                        <code>{r.senta_id}</code>
                        {r.client_ref ? ` · ${r.client_ref}` : ""}
                    </div>
                </div>
            ),
        },
        {
            name: "Renewal Date",
            selector: (r) => r.renewal_date,
            sortable: true,
            cell: (r) => (
                <span className="cell-secondary">
                    {new Date(r.renewal_date).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" })}
                </span>
            ),
            width: "130px",
        },
        {
            name: "Client State",
            selector: (r) => clientStateLabel(r.client_state),
            sortable: true,
            cell: (r) => <span className="cell-secondary">{clientStateLabel(r.client_state)}</span>,
            width: "120px",
        },
        {
            name: "Owner",
            selector: (r) => r.owner || "",
            sortable: true,
            cell: (r) => <span className="cell-secondary">{r.owner || "—"}</span>,
            width: "150px",
        },
        {
            name: "WIP (12m)",
            selector: (r) => r.wip_12m,
            sortable: true,
            right: true,
            cell: (r) => <span className="cell-value">{eur(r.wip_12m)}</span>,
            width: "110px",
        },
        {
            name: "Fees (12m)",
            selector: (r) => r.fees_12m,
            sortable: true,
            right: true,
            cell: (r) => <span className="cell-value">{eur(r.fees_12m)}</span>,
            width: "110px",
        },
        {
            name: "Fees − WIP",
            selector: (r) => r.fees_minus_wip,
            sortable: true,
            right: true,
            cell: (r) => (
                <span style={{ color: r.fees_minus_wip < 0 ? "#f85149" : "#2ea043", fontWeight: 600 }}>
                    {eur(r.fees_minus_wip)}
                </span>
            ),
            width: "120px",
        },
        {
            name: "Recovery",
            selector: (r) => (r.wip_12m === 0 ? -1 : r.recovery_pct),
            sortable: true,
            right: true,
            cell: (r) => (
                <span style={{ color: recoveryColor(r.recovery_pct, r.wip_12m), fontWeight: 600 }}>
                    {r.wip_12m === 0 ? "n/a" : `${r.recovery_pct}%`}
                </span>
            ),
            width: "110px",
        },
    ];

    const exportRows = () =>
        rows.map((r) => ({
            senta_id: r.senta_id,
            client_name: r.client_name,
            renewal_date: r.renewal_date,
            client_state: clientStateLabel(r.client_state),
            owner: r.owner || "",
            wip_12m: r.wip_12m,
            fees_12m: r.fees_12m,
            fees_minus_wip: r.fees_minus_wip,
            recovery_pct: r.recovery_pct,
        }));

    const downloadCSV = () => {
        const data = exportRows();
        const headers = Object.keys(data[0] || { senta_id: "" });
        const esc = (v: any) => {
            const s = String(v ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const csv = [headers.join(","), ...data.map((r) => headers.map((h) => esc((r as any)[h])).join(","))].join("\n");
        const link = document.createElement("a");
        link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
        link.download = `renewals_wip_vs_fees_${renewalMonth}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadXLSX = () => {
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportRows()), "Renewals");
        const summary = [
            { Metric: "Renewal month", Value: monthLabel(renewalMonth) },
            { Metric: "WIP/fees window start", Value: payload?.window_start || "" },
            { Metric: "Clients renewing", Value: totals.clients },
            { Metric: "Total WIP (EUR)", Value: totals.wip },
            { Metric: "Total fees (EUR)", Value: totals.fees },
            { Metric: "Overall recovery %", Value: totals.recovery },
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), "Summary");
        XLSX.writeFile(wb, `renewals_wip_vs_fees_${renewalMonth}.xlsx`);
    };

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div className="page-title">
                        <CalendarClock size={20} />
                        <h1>Renewals: WIP vs Fees</h1>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button className="download-btn" onClick={() => setShowHelp((v) => !v)} title="Notes">
                            <Info size={16} /> {showHelp ? "Hide notes" : "Notes"}
                        </button>
                        <button className="download-btn" onClick={downloadCSV} disabled={!rows.length} title="Export CSV">
                            <Download size={16} /> CSV
                        </button>
                        <button className="download-btn" onClick={downloadXLSX} disabled={!rows.length} title="Export XLSX">
                            <Download size={16} /> XLSX
                        </button>
                    </div>
                </div>
            </div>

            {showHelp && (
                <div
                    style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--border-color)",
                        borderLeft: "3px solid var(--accent-primary)",
                        borderRadius: 6,
                        padding: "14px 18px",
                        marginBottom: 20,
                        color: "var(--text-secondary)",
                        fontSize: 13,
                        lineHeight: 1.55,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, color: "var(--text-primary)", fontWeight: 600, fontSize: 14 }}>
                        <Info size={16} /> How this report is built
                    </div>
                    <ul style={{ margin: "0 0 0 20px", padding: 0 }}>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Renewing clients</strong> — non-archived Senta
                            clients whose renewal date (the client field that drives the proposal-renewal workflow) falls in
                            the selected month. Where that field is empty, the workflow's own next renewal date is used.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Owner</strong> — the client's owner from MKP staff
                            assignments (not the Senta manager). Blank where no owner is assigned in MKP.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Client state</strong> — the report shows active
                            clients by default. Use the state chips to include suspended, on-hold, striking-off or onboarding
                            clients; totals and exports follow whatever is selected.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Window</strong> — the window starts one year ago
                            today and ends at each client's renewal date. For renewals still in the future, data only exists up
                            to today, so the window is shorter than a year; for renewals that have already passed, anything
                            logged or invoiced after the renewal date is excluded. WIP and fees always cover the same period,
                            so recovery % stays like-for-like.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>WIP (12m)</strong> — all submitted/approved time
                            entries logged against the client from one year before today up to and including the client's
                            renewal date, valued at each employee's current billable rate (MKP).
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Fees (12m)</strong> — sales invoices issued in the same
                            window, net of VAT, status paid or awaiting payment (Xero).
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Recovery %</strong> = fees ÷ WIP. Green ≥ 100%, amber
                            70–99%, red &lt; 70%. Clients recovering less than 100% of WIP are highlighted in red and listed
                            first (worst recovery at the top), followed by healthy clients, then clients with no WIP logged.
                        </li>
                    </ul>
                </div>
            )}

            <div className="table-controls" style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-secondary)", fontSize: 13 }}>
                    Renewal month:
                    {/* A real <select>, not <input type="month"> — Safari and
                        Firefox render that as a bare text box with no picker,
                        which left users stuck on the default month. */}
                    <select
                        value={renewalMonth}
                        onChange={(e) => setRenewalMonth(e.target.value)}
                        className="filter-select"
                        style={{ padding: "6px 10px", borderRadius: 4 }}
                    >
                        {monthOptions.map((o) => (
                            <option key={o.month} value={o.month}>
                                {monthLabel(o.month)} ({o.clients} {o.clients === 1 ? "client" : "clients"})
                            </option>
                        ))}
                    </select>
                </label>
                <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180, maxWidth: 320 }}>
                    <Search size={14} style={{ position: "absolute", left: 10, top: 10, color: "var(--text-secondary)" }} />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search client, ref, owner…"
                        style={{
                            width: "100%",
                            padding: "8px 10px 8px 30px",
                            borderRadius: 6,
                            border: "1px solid var(--border-color)",
                            background: "var(--bg-primary)",
                            color: "var(--text-primary)",
                        }}
                    />
                </div>
            </div>

            {stateOptions.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 12 }}>
                    <span style={{ color: "var(--text-secondary)", fontSize: 13, marginRight: 4 }}>Client state:</span>
                    {stateOptions.map((o) => {
                        const active = stateFilter.has(o.code);
                        return (
                            <button
                                key={o.code || "none"}
                                onClick={() => toggleState(o.code)}
                                title={active ? "Click to hide" : "Click to include"}
                                style={{
                                    padding: "5px 12px",
                                    border: "1px solid var(--border-color)",
                                    borderRadius: 16,
                                    background: active ? "var(--accent-primary)" : "var(--bg-secondary)",
                                    color: active ? "#fff" : "var(--text-secondary)",
                                    cursor: "pointer",
                                    fontSize: 13,
                                    fontWeight: active ? 600 : 400,
                                }}
                            >
                                {clientStateLabel(o.code || null)} ({o.count})
                            </button>
                        );
                    })}
                </div>
            )}

            {payload && (
                <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 12 }}>
                    WIP and fees window:{" "}
                    <strong style={{ color: "var(--text-primary)" }}>
                        {new Date(payload.window_start).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" })} → each client's renewal date
                    </strong>
                    {(payload.overdue_clients || 0) > 0 && (
                        <span title="Clients whose Senta renewal date is more than a month in the past — the renewal was never rolled forward. They won't appear in any future month until the date is updated in Senta.">
                            {" "}· {payload.overdue_clients} clients have an out-of-date renewal date in Senta
                        </span>
                    )}
                </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }}>
                <Kpi label={`Renewing in ${monthLabel(renewalMonth)}`} value={String(totals.clients)} sub={`${totals.underRecovered} under-recovered`} />
                <Kpi label="Total WIP (12m)" value={eur(totals.wip)} sub="Time × billable rate" />
                <Kpi label="Total Fees (12m)" value={eur(totals.fees)} sub="Invoiced, net of VAT" />
                <Kpi
                    label="Overall Recovery"
                    value={`${totals.recovery}%`}
                    sub={eur(totals.fees - totals.wip) + " fees − WIP"}
                    accent
                />
            </div>

            {error && (
                <div
                    style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--status-error, #d9534f)",
                        borderRadius: 6,
                        padding: "10px 14px",
                        marginBottom: 12,
                        color: "var(--status-error, #d9534f)",
                    }}
                >
                    {error}
                </div>
            )}

            <div className="renewals-table" style={{ background: "var(--bg-secondary)", borderRadius: 8, border: "1px solid var(--border-color)" }}>
                <style>{`
                    .renewals-table .under-recovered-row,
                    .renewals-table .under-recovered-row:hover {
                        background-color: rgba(239, 68, 68, 0.12) !important;
                        border-left: 3px solid #ef4444;
                    }
                `}</style>
                <DataTable
                    columns={columns}
                    data={rows}
                    customStyles={customStyles}
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    highlightOnHover
                    conditionalRowStyles={[
                        {
                            // Same treatment as Employee Rates' missing-rate rows.
                            when: (r: RenewalRow) => r.wip_12m > 0 && r.recovery_pct < 100,
                            classNames: ["under-recovered-row"],
                        },
                    ]}
                    noDataComponent={
                        <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
                            {payload === null
                                ? "Loading…"
                                : payload.rows.length === 0
                                ? `No clients with a proposal renewal date in ${monthLabel(renewalMonth)}.`
                                : "No clients match the selected client states — use the chips above to include more."}
                        </div>
                    }
                />
            </div>
        </div>
    );
};

const Kpi: React.FC<{ label: string; value: string; sub?: string; accent?: boolean }> = ({ label, value, sub, accent }) => (
    <div
        style={{
            background: accent ? "var(--bg-tertiary)" : "var(--bg-secondary)",
            border: accent ? "1px solid var(--accent-primary)" : "1px solid var(--border-color)",
            borderRadius: 8,
            padding: 14,
        }}
    >
        <div
            style={{
                color: accent ? "var(--accent-primary)" : "var(--text-secondary)",
                fontSize: 12,
                marginBottom: 4,
                fontWeight: accent ? 600 : 400,
                textTransform: accent ? "uppercase" : undefined,
                letterSpacing: accent ? 0.3 : undefined,
            }}
        >
            {label}
        </div>
        <div style={{ fontSize: 22, fontWeight: 600, color: "var(--text-primary)" }}>{value}</div>
        {sub && <div style={{ color: "var(--text-secondary)", fontSize: 13, marginTop: 2 }}>{sub}</div>}
    </div>
);

export default RenewalsWipFees;
